import {
  parsePhoneNumberFromString,
  type CountryCode
} from "libphonenumber-js";

export type PhoneValidationResult =
  | { ok: true }
  | { ok: false; reason: "format" | "dummy_repeat" | "dummy_sequence" };

export type CompanyValidationResult =
  | { ok: true }
  | { ok: false; reason: "empty" | "too_short" };

const MIN_SEQUENCE_LENGTH = 5;
const REPEAT_RATIO_THRESHOLD = 0.8;
const MIN_COMPANY_LENGTH = 2;
// Mirror of backend/src/forms/leadValidation.ts caps — keep in sync.
const MAX_PHONE_INPUT_LENGTH = 50;
const MAX_COMPANY_INPUT_LENGTH = 200;

export function validatePhone(
  rawPhone: string,
  countryIso: string,
  countryCode: string
): PhoneValidationResult {
  if (
    typeof rawPhone !== "string" ||
    rawPhone.length > MAX_PHONE_INPUT_LENGTH
  ) {
    return { ok: false, reason: "format" };
  }

  const digits = rawPhone.replace(/\D/g, "");

  if (hasRepeatedDigits(digits)) return { ok: false, reason: "dummy_repeat" };
  if (hasSequentialRun(digits)) return { ok: false, reason: "dummy_sequence" };

  const e164 = `${countryCode}${digits}`;
  const parsed = parsePhoneNumberFromString(e164, countryIso as CountryCode);
  if (!parsed || !parsed.isValid()) return { ok: false, reason: "format" };

  return { ok: true };
}

export function validateCompany(rawCompany: string): CompanyValidationResult {
  if (
    typeof rawCompany !== "string" ||
    rawCompany.length > MAX_COMPANY_INPUT_LENGTH
  ) {
    return { ok: false, reason: "too_short" };
  }
  const trimmed = rawCompany.trim();
  if (trimmed.length === 0) return { ok: false, reason: "empty" };
  if (trimmed.length < MIN_COMPANY_LENGTH)
    return { ok: false, reason: "too_short" };
  return { ok: true };
}

function hasRepeatedDigits(digits: string): boolean {
  if (digits.length < 4) return false;
  const counts = new Map<string, number>();
  for (const d of digits) counts.set(d, (counts.get(d) ?? 0) + 1);
  const maxCount = Math.max(...counts.values());
  return maxCount / digits.length > REPEAT_RATIO_THRESHOLD;
}

function hasSequentialRun(digits: string): boolean {
  if (digits.length < MIN_SEQUENCE_LENGTH) return false;
  let asc = true;
  let desc = true;
  for (let i = 1; i < digits.length; i++) {
    const diff = digits.charCodeAt(i) - digits.charCodeAt(i - 1);
    if (diff !== 1) asc = false;
    if (diff !== -1) desc = false;
    if (!asc && !desc) return false;
  }
  return asc || desc;
}
